import moment from 'moment-timezone';
import { getIdentifier } from '@zedoc/questionnaire';
import {
  FILTER_TYPE__PROPERTY,
  FILTER_TYPE__ALL_OF,
  FILTER_TYPE__ANY_OF,
  FILTER_CONDITION__INCLUDE,
  FILTER_CONDITION__EXCLUDE,
  FILTER_CONDITION__DATE_EQUALS,
  FILTER_CONDITION__DATE_NOT_EQUAL,
  FILTER_CONDITION__DATE_SAME_OR_AFTER,
  FILTER_CONDITION__DATE_SAME_OR_BEFORE,
  FILTER_CONDITION__DATE_AFTER,
  FILTER_CONDITION__DATE_BEFORE,
} from '../../../common/constants';

const normalizeCustomPDFRecord = (pdf) => ({
  projectId: pdf.projectId,
  milestoneId: pdf.milestoneId,
  completedAt: pdf.milestoneCompletedAt,
  // Drop answers sheets without a questionnaireId — CustomPDF.js filters the
  // same field with `.filter(Boolean)`, so undefined entries are an expected
  // data state and would otherwise crash matchesValue below.
  questionnaireIds: (pdf.answersSheets || [])
    .map(({ questionnaireId }) => questionnaireId)
    .filter(Boolean),
});

const questionnaireMatches = (
  questionnaireIds,
  values,
  { exclude = false } = {},
) => {
  if (!values?.length) {
    return !exclude;
  }

  const matchesValue = (questionnaireId) =>
    values.some(
      (value) =>
        questionnaireId === value ||
        getIdentifier(questionnaireId) === value ||
        questionnaireId.startsWith(`${value}@`),
    );

  const hasMatch = questionnaireIds.some(matchesValue);
  return exclude ? !hasMatch : hasMatch;
};

const getMoment = (state) => {
  if (!state?.threshold) {
    return null;
  }

  if (state.timezone) {
    if (typeof state.threshold === 'string') {
      return moment.tz(state.threshold, state.timezone);
    }
    return moment(state.threshold).tz(state.timezone);
  }

  if (typeof state.threshold === 'string') {
    return moment.parseZone(state.threshold);
  }

  return moment(state.threshold);
};

const dateMatches = (value, condition, state) => {
  const dateValue = value ? moment(value) : null;
  const threshold = getMoment(state);

  if (!dateValue?.isValid() || !threshold?.isValid()) {
    return true;
  }

  switch (condition) {
    case FILTER_CONDITION__DATE_EQUALS:
      return dateValue.isBetween(
        threshold.clone().startOf('day'),
        threshold.clone().endOf('day'),
        null,
        '[]',
      );
    case FILTER_CONDITION__DATE_NOT_EQUAL:
      return !dateValue.isBetween(
        threshold.clone().startOf('day'),
        threshold.clone().endOf('day'),
        null,
        '[]',
      );
    case FILTER_CONDITION__DATE_SAME_OR_AFTER:
      return dateValue.isSameOrAfter(threshold.clone().startOf('day'));
    case FILTER_CONDITION__DATE_AFTER:
      return dateValue.isAfter(threshold.clone().endOf('day'));
    case FILTER_CONDITION__DATE_SAME_OR_BEFORE:
      return dateValue.isSameOrBefore(threshold.clone().endOf('day'));
    case FILTER_CONDITION__DATE_BEFORE:
      return dateValue.isBefore(threshold.clone().startOf('day'));
    default:
      return true;
  }
};

const matchesPropertyFilter = (filter, record) => {
  const fieldId = filter.settings?.id;
  const { condition, state = {} } = filter;

  if (!fieldId) {
    return true;
  }

  if (fieldId === 'questionnaireId') {
    if (condition === FILTER_CONDITION__INCLUDE) {
      return questionnaireMatches(record.questionnaireIds, state.include);
    }
    if (condition === FILTER_CONDITION__EXCLUDE) {
      return questionnaireMatches(record.questionnaireIds, state.exclude, {
        exclude: true,
      });
    }
    return true;
  }

  const fieldValue = record[fieldId];

  if (condition === FILTER_CONDITION__INCLUDE) {
    return !state.include?.length || state.include.includes(fieldValue);
  }

  if (condition === FILTER_CONDITION__EXCLUDE) {
    return !state.exclude?.length || !state.exclude.includes(fieldValue);
  }

  if (fieldId === 'completedAt') {
    return dateMatches(fieldValue, condition, state);
  }

  return true;
};

const matchesFilter = (filter, record) => {
  if (!filter) {
    return true;
  }

  switch (filter.type) {
    case FILTER_TYPE__ALL_OF:
      return (filter.settings?.filters || []).every((childFilter) =>
        matchesFilter(childFilter, record),
      );
    case FILTER_TYPE__ANY_OF:
      return (filter.settings?.filters || []).some((childFilter) =>
        matchesFilter(childFilter, record),
      );
    case FILTER_TYPE__PROPERTY:
      return matchesPropertyFilter(filter, record);
    default:
      return true;
  }
};

const filterCustomPDFs = (customPDFs, filters = []) => {
  if (!filters.length) {
    return customPDFs;
  }

  return customPDFs.filter((pdf) => {
    const record = normalizeCustomPDFRecord(pdf);
    return filters.every((filter) => matchesFilter(filter, record));
  });
};

export default filterCustomPDFs;
